import React from "react";
import { useTranslation } from "react-i18next";

import { ToastComponent } from "../../Components/ToastComponent";
import Spinner from "../../Components/Spinner";
import { formatPhoneNumber } from "../../../Services/string.service";
import SearchInputDocument from "../../Components/Forms/SearchInputDocument";
import {
  useCustomerForm,
  CLIENT_TYPE,
  DISABLED_FIELD_HEIGHT,
} from "../hooks/useCustomerForm";

// Aba "Dados Pessoais" do cliente (issue #453/#490/#535). Desde a divisão em
// Dados Pessoais + Administrativos (issue #535), todo o estado e as chamadas
// de API vivem em useCustomerForm.js — as duas abas compartilham esse hook
// via props.customerForm (quando o pai passa uma instância única) ou criam a
// sua própria (internalForm) caso contrário. Financeiro e Restrição
// Administrativa migraram pra AdministrativeData.jsx.
export default function PersonalData(props) {
  const { t } = useTranslation();
  const internalForm = useCustomerForm(props);
  const form = props.customerForm || internalForm;

  const {
    clientId,
    isNewCustomer,
    clientName,
    setClientName,
    clientTradeName,
    setClientTradeName,
    doc,
    setDoc,
    stateRegistration,
    setStateRegistration,
    municipalRegistration,
    setMunicipalRegistration,
    birthDate,
    setBirthDate,
    phone1,
    setPhone1,
    phone2,
    setPhone2,
    responsibleEmail,
    setResponsibleEmail,
    createdAt,
    updatedAt,
    clientType,
    setClientType,
    isPF,
    isSimpleTax,
    setIsSimpleTax,
    isLoadingClient,
    clientError,
    isSaving,
    toastParams,
    setToastParams,
    fieldErrors,
    setFieldErrors,
    representative,
    salesperson,
    setSalesperson,
    servicePlan,
    setServicePlan,
    plans,
    isLoadingPlans,
    plansError,
    representatives,
    isLoadingRepresentatives,
    representativesError,
    salespeople,
    isLoadingSalespeople,
    salespeopleError,
    handleRepresentativeChange,
    handleSave,
  } = form;

  return (
    <div className="p-3">
      <ToastComponent
        params={toastParams}
        closeToast={() => setToastParams(JSON.stringify({ show: false }))}
      />

      {clientError && (
        <div className="mb-3">
          <small className="text-danger d-block">
            {t("CUSTOMER.PERSONAL_DATA.LOAD_CLIENT_ERROR")}
          </small>
        </div>
      )}

      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <strong>{t("CUSTOMER.PERSONAL_DATA.SECTION_CLIENT_DATA")}</strong>
          <small className="text-muted">
            <span className="text-danger">*</span>{" "}
            {t("CUSTOMER.PERSONAL_DATA.REQUIRED_FIELDS_HINT")}
          </small>
        </div>
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-2">
              <label className="form-label">
                {t("CUSTOMER.PERSONAL_DATA.CLIENT_ID")}
              </label>
              <input
                type="text"
                className="form-control"
                style={DISABLED_FIELD_HEIGHT}
                value={clientId ?? ""}
                disabled
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">
                {t("CUSTOMER.PERSONAL_DATA.CLIENT_TYPE")}
              </label>
              <select
                className="form-select"
                style={!isNewCustomer ? DISABLED_FIELD_HEIGHT : undefined}
                value={clientType}
                onChange={(e) => {
                  const newType = e.target.value;
                  setClientType(newType);
                  if (
                    newType === CLIENT_TYPE.PF &&
                    doc.replace(/\D/g, "").length > 11
                  ) {
                    setDoc("");
                  }
                }}
                disabled={isLoadingClient || !isNewCustomer}
              >
                <option value={CLIENT_TYPE.PF}>
                  {t("CUSTOMER.PERSONAL_DATA.CLIENT_TYPE_PF")}
                </option>
                <option value={CLIENT_TYPE.PJ}>
                  {t("CUSTOMER.PERSONAL_DATA.CLIENT_TYPE_PJ")}
                </option>
              </select>
            </div>
            {!isPF && (
              <div className="col-md-3 d-flex">
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="personalDataSimpleTax"
                    checked={isSimpleTax}
                    onChange={(e) => setIsSimpleTax(e.target.checked)}
                    disabled={isLoadingClient}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="personalDataSimpleTax"
                  >
                    {t("CUSTOMER.PERSONAL_DATA.SIMPLE_TAX")}
                  </label>
                </div>
              </div>
            )}
          </div>

          <div className="row g-3 mt-1">
            <div className="col-md-3">
              <SearchInputDocument
                key={isPF ? "doc-pf" : "doc-pj"}
                name="doc"
                label={`${isPF ? "CPF" : "CNPJ"} *`}
                value={doc}
                onChange={(e) => {
                  setDoc(e.target.value.replace(/\D/g, ""));
                  if (fieldErrors.doc)
                    setFieldErrors((prev) => ({ ...prev, doc: false }));
                }}
                disabled={isLoadingClient}
                labelPosition="top"
                isInvalid={fieldErrors.doc}
              />
              {fieldErrors.doc && (
                <div className="text-danger small mt-1">
                  {t("CUSTOMER.PERSONAL_DATA.DOC_REQUIRED")}
                </div>
              )}
            </div>

            <div className="col-md-5">
              <label className="form-label">
                {t(isPF ? "COMMON.NAME" : "CUSTOMER.PERSONAL_DATA.CLIENT_NAME")}{" "}
                <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-control${
                  fieldErrors.clientName ? " is-invalid" : ""
                }`}
                value={clientName}
                onChange={(e) => {
                  setClientName(e.target.value);
                  if (fieldErrors.clientName)
                    setFieldErrors((prev) => ({ ...prev, clientName: false }));
                }}
                disabled={isLoadingClient}
              />
              {fieldErrors.clientName && (
                <div className="invalid-feedback">
                  {t("CUSTOMER.PERSONAL_DATA.CLIENT_NAME_REQUIRED")}
                </div>
              )}
            </div>
          </div>

          {!isPF && (
            <div className="row g-3 mt-1">
              <div className="col-md-4">
                <label className="form-label">
                  {t("CUSTOMER.PERSONAL_DATA.CLIENT_TRADE_NAME")}
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={clientTradeName}
                  onChange={(e) => setClientTradeName(e.target.value)}
                  disabled={isLoadingClient}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">
                  {t("CUSTOMER.PERSONAL_DATA.STATE_REGISTRATION")}
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={stateRegistration}
                  onChange={(e) => setStateRegistration(e.target.value)}
                  disabled={isLoadingClient}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">
                  {t("CUSTOMER.PERSONAL_DATA.MUNICIPAL_REGISTRATION")}
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={municipalRegistration}
                  onChange={(e) => setMunicipalRegistration(e.target.value)}
                  disabled={isLoadingClient}
                />
              </div>
            </div>
          )}

          <div className="row g-3 mt-1">
            <div className="col-md-3">
              <label className="form-label">
                {t("CUSTOMER.PERSONAL_DATA.BIRTH_DATE")}
              </label>
              <input
                type="date"
                className="form-control"
                value={birthDate ? birthDate.slice(0, 10) : ""}
                onChange={(e) => setBirthDate(e.target.value)}
                disabled={isLoadingClient}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">
                {t("CUSTOMER.PERSONAL_DATA.EMAIL")}
              </label>
              <input
                type="email"
                className="form-control"
                value={responsibleEmail}
                onChange={(e) => setResponsibleEmail(e.target.value)}
                disabled={isLoadingClient}
              />
            </div>
          </div>

          <div className="row g-3 mt-1">
            <div className="col-md-3">
              <label className="form-label">
                {t("CUSTOMER.PERSONAL_DATA.PHONE")}
              </label>
              <input
                type="text"
                className="form-control"
                value={formatPhoneNumber(phone1)}
                onChange={(e) => setPhone1(formatPhoneNumber(e.target.value))}
                disabled={isLoadingClient}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">
                {t("CUSTOMER.PERSONAL_DATA.CELLPHONE")}
              </label>
              <input
                type="text"
                className="form-control"
                value={formatPhoneNumber(phone2)}
                onChange={(e) => setPhone2(formatPhoneNumber(e.target.value))}
                disabled={isLoadingClient}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">
                {t("CUSTOMER.PERSONAL_DATA.CREATED_AT")}
              </label>
              <input
                type="text"
                className="form-control"
                style={DISABLED_FIELD_HEIGHT}
                value={
                  createdAt ? new Date(createdAt).toLocaleString("pt-BR") : ""
                }
                disabled
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">
                {t("CUSTOMER.PERSONAL_DATA.UPDATED_AT")}
              </label>
              <input
                type="text"
                className="form-control"
                style={DISABLED_FIELD_HEIGHT}
                value={
                  updatedAt ? new Date(updatedAt).toLocaleString("pt-BR") : ""
                }
                disabled
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header">
          <strong>{t("CUSTOMER.PERSONAL_DATA.SECTION_COMMERCIAL")}</strong>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">
                {t("CUSTOMER.PERSONAL_DATA.REPRESENTATIVE")}{" "}
                <span className="text-danger">*</span>
              </label>
              {isLoadingRepresentatives ? (
                <Spinner />
              ) : (
                <select
                  className={`form-select${
                    fieldErrors.representative ? " is-invalid" : ""
                  }`}
                  value={representative}
                  onChange={(e) => {
                    handleRepresentativeChange(e.target.value);
                    if (fieldErrors.representative) {
                      setFieldErrors((prev) => ({
                        ...prev,
                        representative: false,
                      }));
                    }
                  }}
                  disabled={representativesError}
                >
                  <option value="">
                    {t("CUSTOMER.PERSONAL_DATA.SELECT_PLACEHOLDER")}
                  </option>
                  {representatives.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              )}
              {fieldErrors.representative && (
                <div className="invalid-feedback">
                  {t("CUSTOMER.PERSONAL_DATA.REPRESENTATIVE_REQUIRED")}
                </div>
              )}
              {representativesError && (
                <small className="text-danger d-block mt-1">
                  {t("CUSTOMER.PERSONAL_DATA.LOAD_REPRESENTATIVES_ERROR")}
                </small>
              )}
            </div>
            <div className="col-md-4">
              <label className="form-label">
                {t("CUSTOMER.PERSONAL_DATA.COMMERCIAL")}{" "}
                <span className="text-danger">*</span>
              </label>
              {isLoadingSalespeople ? (
                <Spinner />
              ) : (
                <select
                  className={`form-select${
                    fieldErrors.salesperson ? " is-invalid" : ""
                  }`}
                  value={salesperson}
                  onChange={(e) => {
                    setSalesperson(e.target.value);
                    if (fieldErrors.salesperson) {
                      setFieldErrors((prev) => ({
                        ...prev,
                        salesperson: false,
                      }));
                    }
                  }}
                  disabled={salespeopleError || !representative}
                >
                  <option value="">
                    {t("CUSTOMER.PERSONAL_DATA.SELECT_PLACEHOLDER")}
                  </option>
                  {salespeople.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              )}
              {fieldErrors.salesperson && (
                <div className="invalid-feedback">
                  {t("CUSTOMER.PERSONAL_DATA.SALESPERSON_REQUIRED")}
                </div>
              )}
              {salespeopleError && (
                <small className="text-danger d-block mt-1">
                  {t("CUSTOMER.PERSONAL_DATA.LOAD_SALESPEOPLE_ERROR")}
                </small>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header">
          <strong>{t("CUSTOMER.PERSONAL_DATA.SECTION_SERVICE_PLAN")}</strong>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">
                {t("CUSTOMER.PERSONAL_DATA.SERVICE_PLAN")}{" "}
                <span className="text-danger">*</span>
              </label>
              {isLoadingPlans ? (
                <Spinner />
              ) : (
                <select
                  className={`form-select${
                    fieldErrors.servicePlan ? " is-invalid" : ""
                  }`}
                  value={servicePlan}
                  onChange={(e) => {
                    setServicePlan(e.target.value);
                    if (fieldErrors.servicePlan) {
                      setFieldErrors((prev) => ({
                        ...prev,
                        servicePlan: false,
                      }));
                    }
                  }}
                  disabled={plansError}
                >
                  <option value="">
                    {t("CUSTOMER.PERSONAL_DATA.SELECT_PLACEHOLDER")}
                  </option>
                  {plans.map((plan) => (
                    <option
                      key={plan.servicePlanId}
                      value={plan.servicePlanId}
                    >
                      {plan.servicePlanName}
                    </option>
                  ))}
                </select>
              )}
              {fieldErrors.servicePlan && (
                <div className="invalid-feedback">
                  {t("CUSTOMER.PERSONAL_DATA.SERVICE_PLAN_REQUIRED")}
                </div>
              )}
              {plansError && (
                <small className="text-danger d-block mt-1">
                  {t("CUSTOMER.PERSONAL_DATA.LOAD_PLANS_ERROR")}
                </small>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-end">
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? t("CUSTOMER.PERSONAL_DATA.UPDATING") : t("COMMON.SAVE")}
        </button>
      </div>
    </div>
  );
}
